import React, { useState } from "react";
import { Container, Row, Col, Card } from "react-bootstrap";
import Button from "../components/Button";
import TalentDialog from "../components/Dialogs/TalentDialog";
import TalentSummary from "../components/TalentSummary";
import TalentList from "../components/TalentList";
import TalentDebugger from "../components/TalentDebugger";
import usePlayerTalents from "../hooks/usePlayerTalents";
import { useAppTranslation } from "../i18n/useAppTranslation";

/**
 * Comprehensive Example of Player Talent System Integration
 *
 * This example demonstrates:
 * 1. How to use the usePlayerTalents hook
 * 2. How to integrate TalentDialog for full talent management
 * 3. How to use TalentSummary for compact displays
 * 4. How to use TalentList for custom layouts
 * 5. How to handle talent upgrades and interactions
 */

const TalentSystemExample: React.FC = () => {
  const { t } = useAppTranslation("common");
  const [talentDialogOpen, setTalentDialogOpen] = useState(false);
  const [talentDialogTab, setTalentDialogTab] = useState<"owned" | "available">(
    "owned"
  );

  // Use the talent hook to get all talent data and actions
  const {
    playerTalents,
    availableTalents,
    loading,
    upgrading,
    error,
    upgradeTalent,
    refreshTalents,
    canUpgradeTalent,
    getTalentById,
  } = usePlayerTalents();

  // Filter talents for different displays (with safety checks)
  const activeTalents = Array.isArray(playerTalents)
    ? playerTalents.filter((talent) => talent.level > 0)
    : [];
  const maxLevelTalents = Array.isArray(activeTalents)
    ? activeTalents.filter((talent) => talent.level >= talent.maxLevel)
    : [];

  // Handle opening talent dialog with specific tab
  const openTalentDialog = (tab: "owned" | "available" = "owned") => {
    setTalentDialogTab(tab);
    setTalentDialogOpen(true);
  };

  // Handle talent upgrade from different components
  const handleTalentUpgrade = async (talentId: any) => {
    const success = await upgradeTalent(talentId);
    if (success) {
      console.log(`Successfully upgraded talent ${talentId}`);
    }
  };

  return (
    <Container fluid className="talent-system-example py-4">
      <h1 className="mb-4">{t("examples.talentSystem.title")}</h1>

      {/* Error Display */}
      {error && (
        <div className="alert alert-danger mb-4">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}

      <Row className="g-4">
        {/* Example 1: Header Integration */}
        <Col xs={12}>
          <Card bg="dark" text="light">
            <Card.Header>
              <h5>
                <i className="fas fa-star me-2"></i>Header Integration Example
              </h5>
            </Card.Header>
            <Card.Body>
              <p className="text-muted mb-3">
                This shows how to integrate talent summary in a header or
                navigation bar:
              </p>

              <div className="d-flex justify-content-between align-items-center p-3 bg-dark rounded">
                <div className="d-flex align-items-center gap-3">
                  <h6 className="mb-0">{t("examples.talentSystem.chainOfLegends")}</h6>
                  <div className="vr"></div>
                  <TalentSummary
                    onOpenTalentDialog={() => openTalentDialog("owned")}
                    maxDisplay={4}
                    iconSize={28}
                  />
                </div>
                <Button
                  variant="primary"
                  size="small"
                  onClick={() => openTalentDialog("available")}
                >
                  <i className="fas fa-plus me-1"></i>Unlock More
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Example 2: Sidebar Integration */}
        <Col lg={4}>
          <Card bg="dark" text="light" className="h-100">
            <Card.Header>
              <h5>
                <i className="fas fa-sidebar me-2"></i>Sidebar Integration
              </h5>
            </Card.Header>
            <Card.Body>
              <p className="text-muted small mb-3">
                Perfect for side panels or character sheets:
              </p>

              <TalentSummary
                onOpenTalentDialog={() => openTalentDialog()}
                maxDisplay={6}
                orientation="vertical"
                iconSize={36}
                showButton={true}
              />

              <hr />

              <div
                style={{ maxWidth: "150px" }}
                className="d-grid mx-auto gap-2"
              >
                <Button
                  variant="wide-primary"
                  size="small"
                  onClick={() => openTalentDialog("owned")}
                >
                  <i className="fas fa-eye me-1"></i>View My Talents
                </Button>
                <Button
                  variant="wide-primary"
                  size="small"
                  onClick={() => openTalentDialog("available")}
                >
                  <i className="fas fa-star me-1"></i>Available Talents
                </Button>
                <Button
                  variant="wide"
                  size="small"
                  onClick={refreshTalents}
                  loading={loading}
                >
                  <i className="fas fa-sync me-1"></i>Refresh
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Example 3: Statistics and Quick Actions */}
        <Col lg={8}>
          <Card bg="dark" text="light" className="h-100">
            <Card.Header>
              <h5>
                <i className="fas fa-chart-bar me-2"></i>Talent Statistics
              </h5>
            </Card.Header>
            <Card.Body>
              <Row className="g-3 mb-4">
                <Col sm={3}>
                  <div className="text-center p-3 bg-primary bg-opacity-10 rounded">
                    <div className="h4 mb-1 text-primary">
                      {activeTalents.length}
                    </div>
                    <div className="small text-muted">
                      {t("examples.talentSystem.stats.activeTalents")}
                    </div>
                  </div>
                </Col>
                <Col sm={3}>
                  <div className="text-center p-3 bg-warning bg-opacity-10 rounded">
                    <div className="h4 mb-1 text-warning">
                      {maxLevelTalents.length}
                    </div>
                    <div className="small text-muted">
                      {t("examples.talentSystem.stats.maxLevel")}
                    </div>
                  </div>
                </Col>
                <Col sm={3}>
                  <div className="text-center p-3 bg-success bg-opacity-10 rounded">
                    <div className="h4 mb-1 text-success">
                      {Array.isArray(availableTalents)
                        ? availableTalents.filter((t) => t.isAvailable).length
                        : 0}
                    </div>
                    <div className="small text-muted">
                      {t("examples.talentSystem.stats.available")}
                    </div>
                  </div>
                </Col>
                <Col sm={3}>
                  <div className="text-center p-3 bg-info bg-opacity-10 rounded">
                    <div className="h4 mb-1 text-info">
                      {Array.isArray(availableTalents)
                        ? availableTalents.filter((t) => t.canAfford).length
                        : 0}
                    </div>
                    <div className="small text-muted">
                      {t("examples.talentSystem.stats.canAfford")}
                    </div>
                  </div>
                </Col>
              </Row>

              <div className="d-flex gap-2">
                <Button
                  variant="wide-primary"
                  className="bg-100"
                  onClick={() => openTalentDialog("owned")}
                  disabled={loading}
                >
                  Manage Talents
                </Button>
                <Button
                  variant="wide-success"
                  className="bg-100 mt-1"
                  onClick={() => openTalentDialog("available")}
                  disabled={loading}
                >
                  Unlock New
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Example 4: Custom List Layout */}
        <Col xs={12}>
          <Card bg="dark" text="light">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5>
                <i className="fas fa-list me-2"></i>My Active Talents
              </h5>
              <Button
                variant="wide-primary"
                size="small"
                onClick={() => openTalentDialog("owned")}
              >
                View All
              </Button>
            </Card.Header>
            <Card.Body>
              {activeTalents.length > 0 ? (
                <TalentList
                  talents={activeTalents}
                  loading={loading}
                  onUpgradeClick={handleTalentUpgrade}
                  onViewClick={(talentId) => {
                    console.log(`Viewing talent: ${talentId}`);
                    openTalentDialog("owned");
                  }}
                  canUpgradeTalent={canUpgradeTalent}
                  upgradingTalent={upgrading}
                  compact={false}
                  showUpgradeButtons={true}
                  columnsPerRow={3}
                  emptyMessage="No active talents found. Click 'Unlock New' to get started!"
                />
              ) : (
                <div className="text-center py-5">
                  <i className="fas fa-star fa-3x mb-3 text-muted opacity-50"></i>
                  <h5 className="text-muted">
                    {t("examples.talentSystem.noActiveTalents")}
                  </h5>
                  <p className="text-muted mb-3">
                    Unlock your first talent to start gaining powerful bonuses!
                  </p>
                  <Button
                    variant="primary"
                    onClick={() => openTalentDialog("available")}
                  >
                    <i className="fas fa-star me-2"></i>Browse Available Talents
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Example 5: Quick Action Panel */}
        <Col xs={12}>
          <Card bg="dark" text="light">
            <Card.Body>
              <h6 className="mb-3">{t("examples.talentSystem.quickActions")}</h6>
              <div className="d-flex flex-wrap gap-2">
                <Button
                  variant="primary"
                  size="small"
                  onClick={() => openTalentDialog("owned")}
                  loading={loading}
                >
                  <i className="fas fa-eye me-1"></i>View Talents (
                  {activeTalents.length})
                </Button>

                <Button
                  variant="success"
                  size="small"
                  onClick={() => openTalentDialog("available")}
                  loading={loading}
                >
                  <i className="fas fa-unlock me-1"></i>
                  Available (
                  {availableTalents.filter((t) => t.isAvailable).length})
                </Button>

                <Button
                  variant="wide"
                  size="small"
                  onClick={() => {
                    if (Array.isArray(playerTalents)) {
                      const upgradeable = playerTalents.find((t) =>
                        canUpgradeTalent(t.talentId)
                      );
                      if (upgradeable) {
                        handleTalentUpgrade(upgradeable.talentId);
                      }
                    }
                  }}
                  disabled={
                    !Array.isArray(playerTalents) ||
                    !playerTalents.some((t) => canUpgradeTalent(t.talentId)) ||
                    upgrading !== null
                  }
                  loading={upgrading !== null}
                >
                  <i className="fas fa-level-up-alt me-1"></i>Quick Upgrade
                </Button>

                <Button size="small" onClick={refreshTalents} loading={loading}>
                  <i className="fas fa-sync me-1"></i>Refresh
                </Button>
              </div>

              {/* Hook Usage Example */}
              <div className="mt-3 pt-3 border-top">
                <h6 className="small text-muted mb-2">
                  Hook Usage Example (Console):
                </h6>
                <div className="d-flex flex-wrap gap-2">
                  <Button
                    variant="wide-primary"
                    size="small"
                    onClick={() =>
                      console.log("Player Talents:", playerTalents)
                    }
                  >
                    Log Player Talents
                  </Button>
                  <Button
                    variant="wide-primary"
                    size="small"
                    onClick={() =>
                      console.log("Available Talents:", availableTalents)
                    }
                  >
                    Log Available Talents
                  </Button>
                  <Button
                    variant="wide"
                    size="small"
                    onClick={() => {
                      if (Array.isArray(playerTalents)) {
                        playerTalents.forEach((talent) => {
                          console.log(
                            `${
                              talent.talentId
                            }: Can upgrade = ${canUpgradeTalent(
                              talent.talentId
                            )}`
                          );
                        });
                      } else {
                        console.log(
                          "PlayerTalents is not an array:",
                          playerTalents
                        );
                      }
                    }}
                  >
                    Check Upgrade Status
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Temporary Debugger Component */}
      <Row className="mt-4">
        <Col xs={12}>
          <TalentDebugger />
        </Col>
      </Row>

      {/* Main Talent Dialog */}
      <TalentDialog
        open={talentDialogOpen}
        onClose={() => setTalentDialogOpen(false)}
        initialTab={talentDialogTab}
      />
    </Container>
  );
};

export default TalentSystemExample;
